import { useCallback, useEffect, useState } from 'react'
import { OfflineModal } from '@/shared/ui/molecules/offline-modal'
import {
  OFFLINE_NAVIGATE_BLOCKED_EVENT,
  CHUNK_LOAD_FAILED_EVENT,
  type OfflineNavigateBlockedDetail,
} from '@/shared/lib/safe-navigate'

interface ChunkLoadFailedDetail {
  reset: () => void
}

/**
 * Global controller that renders the OfflineModal in response to:
 *   1. `app:offline-navigate-blocked` — dispatched by `useSafeNavigate` when
 *       the user tries to navigate while offline. `retry` re-runs the navigation.
 *   2. `app:chunk-load-failed` — dispatched by `ErrorFallback` when a lazy
 *       chunk load fails while offline. `retry` calls `resetErrorBoundary`.
 *
 * Only one modal is visible at a time. When the connection is restored,
 * OfflineModal auto-calls `onRetry` (see its useEffect on `isOnline`).
 */
export function OfflineModalController() {
  const [isOpen, setIsOpen] = useState(false)
  const [retryAction, setRetryAction] = useState<(() => void) | null>(null)

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setRetryAction(null)
  }, [])

  const handleRetry = useCallback(() => {
    // Invoke the pending action (navigate or boundary reset) then close
    retryAction?.()
    setIsOpen(false)
    setRetryAction(null)
  }, [retryAction])

  useEffect(() => {
    const onNavBlocked = (e: Event) => {
      const detail = (e as CustomEvent<OfflineNavigateBlockedDetail>).detail
      setRetryAction(() => detail?.retry ?? (() => {}))
      setIsOpen(true)
    }

    const onChunkFailed = (e: Event) => {
      const detail = (e as CustomEvent<ChunkLoadFailedDetail>).detail
      setRetryAction(() => detail?.reset ?? (() => {}))
      setIsOpen(true)
    }

    window.addEventListener(OFFLINE_NAVIGATE_BLOCKED_EVENT, onNavBlocked)
    window.addEventListener(CHUNK_LOAD_FAILED_EVENT, onChunkFailed)

    return () => {
      window.removeEventListener(OFFLINE_NAVIGATE_BLOCKED_EVENT, onNavBlocked)
      window.removeEventListener(CHUNK_LOAD_FAILED_EVENT, onChunkFailed)
    }
  }, [])

  return (
    <OfflineModal
      isOpen={isOpen}
      onClose={handleClose}
      onRetry={handleRetry}
    />
  )
}

export default OfflineModalController
